import { PRESETS, StockfishWorker } from "@/features/analysis";
import {
  predictMaia2Position,
  type Maia2Prediction,
  type Maia2PredictionOptions,
} from "@/features/board/temporary-play";
import { getEngineUrl, type AnalysisPreset } from "@/features/analysis/analysis-presets";
import type { PositionEval, StockfishSearchProgress } from "@/features/analysis/stockfish-worker";
import {
  applyLegalMove,
  applySanMove,
  BROWSER_ENGINE_DEPTHS,
  piecesInPosition,
  type BrowserEngineArtifact,
  type BrowserEngineLine,
  type BrowserEngineRequest,
  type BrowserEngineSnapshot,
} from "@chessla/shared";
import type { BrowserEngineProgress } from "./browser-engine.contract";
import {
  LICHESS_TABLEBASE_BUILD,
  LichessTablebaseClient,
  type BrowserTablebaseClient,
  type BrowserTablebaseProbe,
} from "./browser-tablebase.client";

export type { BrowserTablebaseClient } from "./browser-tablebase.client";

export interface BrowserEngineWorker {
  readonly engineBuild?: string | null;
  init: (preset: AnalysisPreset) => Promise<void>;
  evaluate: (
    fen: string,
    depth: number,
    multiPv?: number,
    onProgress?: (progress: StockfishSearchProgress) => void,
    includeWdl?: boolean,
  ) => Promise<PositionEval>;
  stop: () => void;
  destroy: () => void;
}

export type BrowserPracticalPredictor = (
  fen: string,
  activeRating: number,
  opponentRating: number,
  options?: Maia2PredictionOptions,
) => Promise<Maia2Prediction | null>;

interface ActiveCalculation {
  requestId: string;
  controller: AbortController;
}

const cancellation = (): DOMException => new DOMException("Nikola calculation cancelled", "AbortError");

const abortable = <T>(value: Promise<T>, signal: AbortSignal): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const abort = () => reject(cancellation());
    signal.addEventListener("abort", abort, { once: true });
    value.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
  });

const replay = (fen: string, moves: readonly string[]): { fen: string; sans: string[] } | null => {
  let current = fen;
  const sans: string[] = [];
  for (const move of moves) {
    const applied = applyLegalMove(current, move) ?? applySanMove(current, move);
    if (!applied) return null;
    sans.push(applied.san);
    current = applied.fen;
  }
  return { fen: current, sans };
};

const normalizeEvaluation = (
  cp: number,
  mate: number | null,
  samePerspective: boolean,
): BrowserEngineLine["evaluation"] => ({
  cp: mate === null ? (samePerspective ? cp : -cp) : null,
  mate: mate === null ? null : samePerspective ? mate : -mate,
});

const playerToMove = (fen: string, color: BrowserEngineRequest["source"]["playerColor"]): boolean =>
  (fen.split(" ")[1] === "w") === (color === "white");

const normalizeWdl = (wdl: BrowserEngineLine["wdl"], samePerspective: boolean): BrowserEngineLine["wdl"] =>
  !wdl || samePerspective ? wdl : { win: wdl.loss, draw: wdl.draw, loss: wdl.win };

const evaluationValue = ({ cp, mate }: BrowserEngineLine["evaluation"]): number =>
  mate == null ? (cp ?? 0) : mate > 0 ? 1_000_000 - mate : -1_000_000 - mate;

export class BrowserEngineService {
  private worker: BrowserEngineWorker | null = null;
  private workerPreset: AnalysisPreset | null = null;
  private workerPromise: Promise<BrowserEngineWorker> | null = null;
  private active: ActiveCalculation | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly createWorker: () => BrowserEngineWorker = () => new StockfishWorker(),
    private readonly now: () => number = () => performance.now(),
    private readonly tablebase: BrowserTablebaseClient = new LichessTablebaseClient(),
    private readonly predictPractical: BrowserPracticalPredictor = predictMaia2Position,
  ) {}

  calculate(
    request: BrowserEngineRequest,
    onProgress?: (progress: BrowserEngineProgress) => void,
    externalSignal?: AbortSignal,
  ): Promise<BrowserEngineArtifact> {
    this.active?.controller.abort();
    const active: ActiveCalculation = { requestId: request.requestId, controller: new AbortController() };
    this.active = active;
    const cancel = () => active.controller.abort();
    externalSignal?.addEventListener("abort", cancel, { once: true });

    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      active.controller.abort();
    }, request.limits.maxTimeMs);
    const run = this.queue
      .then(() => this.run(request, active, onProgress))
      .catch((error: unknown) => {
        if (timedOut) throw new Error("Nikola calculation timed out.");
        throw error;
      });
    this.queue = run.catch(() => undefined);
    return run.finally(() => {
      clearTimeout(timeout);
      externalSignal?.removeEventListener("abort", cancel);
      if (this.active === active) this.active = null;
    });
  }

  private async getWorker(): Promise<BrowserEngineWorker> {
    if (this.worker) return this.worker;
    this.workerPromise ??= this.initializeWorker();
    return this.workerPromise;
  }

  private async initializeWorker(): Promise<BrowserEngineWorker> {
    const preset = PRESETS[1];
    const worker = this.createWorker();
    try {
      await worker.init(preset);
      this.worker = worker;
      this.workerPreset = preset;
      return worker;
    } catch {
      worker.destroy();
      const fallbackPreset = { ...preset, threads: 1, forceSingleThread: true };
      const fallback = this.createWorker();
      await fallback.init(fallbackPreset);
      this.worker = fallback;
      this.workerPreset = fallbackPreset;
      return fallback;
    }
  }

  private async run(
    request: BrowserEngineRequest,
    active: ActiveCalculation,
    onProgress?: (progress: BrowserEngineProgress) => void,
  ): Promise<BrowserEngineArtifact> {
    const { signal } = active.controller;
    if (signal.aborted) throw cancellation();
    const startedAt = this.now();
    onProgress?.({
      requestId: request.requestId,
      phase: "initializing",
      depth: 0,
      targetDepth: request.limits.depth,
      nodes: 0,
    });
    if (request.kind === "tablebase") return this.runTablebase(request, active, startedAt, onProgress);
    if (request.kind === "move_comparison") return this.runComparison(request, active, startedAt, onProgress);
    const practicalPromise = request.practicalRequest
      ? abortable(
          this.predictPractical(
            request.source.fen,
            request.practicalRequest.activeRating,
            request.practicalRequest.opponentRating,
            { signal },
          ),
          signal,
        )
      : Promise.resolve(null);
    const worker = await this.getWorker();
    if (signal.aborted || this.active !== active) throw cancellation();

    const branch = replay(request.source.fen, request.branch ?? []);
    if (!branch) throw new Error("The requested branch is illegal in this position.");
    const depths = BROWSER_ENGINE_DEPTHS.filter((depth) => depth <= request.limits.depth);
    let result: PositionEval | null = null;
    const snapshots: BrowserEngineSnapshot[] = [];
    const stop = () => {
      worker.stop();
      worker.destroy();
      if (this.worker === worker) {
        this.worker = null;
        this.workerPromise = null;
        this.workerPreset = null;
      }
    };
    signal.addEventListener("abort", stop, { once: true });
    try {
      for (const depth of depths) {
        if (signal.aborted || this.active !== active) throw cancellation();
        result = await abortable(
          worker.evaluate(
            branch.fen,
            depth,
            request.limits.multiPv,
            (progress) =>
              onProgress?.({
                requestId: request.requestId,
                phase: "calculating",
                depth: progress.depth,
                targetDepth: request.limits.depth,
                nodes: progress.nodes,
              }),
            request.limits.includeWdl,
          ),
          signal,
        );
        snapshots.push({ depth: result.depth, lines: this.regularLines(request, branch, result) });
      }
    } finally {
      signal.removeEventListener("abort", stop);
    }
    if (!result || signal.aborted || this.active !== active) throw cancellation();

    const lines = this.regularLines(request, branch, result);
    if (lines.some((line) => line.moves.length === 0)) throw new Error("Nikola calculation returned no legal line.");
    const practicalPrediction = await practicalPromise;
    if (request.practicalRequest && !practicalPrediction)
      throw new Error("Nikola practical estimate is unavailable for this position.");

    const artifact: BrowserEngineArtifact = {
      ...request,
      engine: this.stockfishProvenance(worker),
      reached: {
        depth: result.depth,
        nodes: result.nodes,
        timeMs: Math.max(0, Math.round(this.now() - startedAt)),
      },
      lines,
      snapshots,
      ...(request.practicalRequest && practicalPrediction
        ? {
            practical: {
              ...request.practicalRequest,
              candidates: practicalPrediction.candidates.slice(0, 5),
              source: { name: "Maia 2" as const, version: "rapid-fp16-v1" as const },
            },
          }
        : {}),
      createdAt: new Date().toISOString(),
    };
    onProgress?.({
      requestId: request.requestId,
      phase: "complete",
      depth: result.depth,
      targetDepth: request.limits.depth,
      nodes: result.nodes,
    });
    return artifact;
  }

  private regularLines(
    request: BrowserEngineRequest,
    branch: { fen: string; sans: string[] },
    result: PositionEval,
  ): BrowserEngineLine[] {
    const samePerspective = playerToMove(branch.fen, request.source.playerColor);
    const rawLines = result.pvLines.length
      ? [...result.pvLines]
      : [
          {
            rank: 1,
            depth: result.depth,
            cp: result.cp,
            mate: result.mate,
            moveUci: result.bestMove,
            continuation: result.pv.slice(1),
          },
        ];
    return rawLines
      .sort((left, right) => left.rank - right.rank)
      .map((line) => {
        const continuation = replay(
          branch.fen,
          [line.moveUci, ...line.continuation].slice(0, request.limits.maxPlies - branch.sans.length),
        );
        if (!continuation) throw new Error("Nikola calculation returned an illegal line.");
        return {
          rank: line.rank,
          moves: [...branch.sans, ...continuation.sans],
          evaluation: normalizeEvaluation(line.cp, line.mate, samePerspective),
          ...(line.wdl ? { wdl: normalizeWdl(line.wdl, samePerspective) } : {}),
        };
      });
  }

  private async runComparison(
    request: BrowserEngineRequest,
    active: ActiveCalculation,
    startedAt: number,
    onProgress?: (progress: BrowserEngineProgress) => void,
  ): Promise<BrowserEngineArtifact> {
    const { signal } = active.controller;
    const candidates = request.candidateMoves?.map((move) => replay(request.source.fen, [move])) ?? [];
    if (candidates.length === 0 || candidates.some((candidate) => !candidate))
      throw new Error("The requested move comparison contains an illegal move.");
    const legalCandidates = candidates as { fen: string; sans: string[] }[];
    const worker = await this.getWorker();
    if (signal.aborted || this.active !== active) throw cancellation();
    const depths = BROWSER_ENGINE_DEPTHS.filter((depth) => depth <= request.limits.depth);
    let finalResults: PositionEval[] = [];
    const snapshots: BrowserEngineSnapshot[] = [];
    const stop = () => {
      worker.stop();
      worker.destroy();
      if (this.worker === worker) {
        this.worker = null;
        this.workerPromise = null;
        this.workerPreset = null;
      }
    };
    signal.addEventListener("abort", stop, { once: true });
    try {
      for (const depth of depths) {
        const depthResults: PositionEval[] = [];
        let completedNodes = 0;
        for (const candidate of legalCandidates) {
          if (signal.aborted || this.active !== active) throw cancellation();
          const result = await abortable(
            worker.evaluate(
              candidate.fen,
              depth,
              1,
              (progress) =>
                onProgress?.({
                  requestId: request.requestId,
                  phase: "calculating",
                  depth: progress.depth,
                  targetDepth: request.limits.depth,
                  nodes: completedNodes + progress.nodes,
                }),
              request.limits.includeWdl,
            ),
            signal,
          );
          depthResults.push(result);
          completedNodes += result.nodes;
        }
        finalResults = depthResults;
        snapshots.push({ depth, lines: this.comparisonLines(request, legalCandidates, depthResults) });
      }
    } finally {
      signal.removeEventListener("abort", stop);
    }
    if (finalResults.length !== legalCandidates.length || signal.aborted || this.active !== active)
      throw cancellation();

    const lines = this.comparisonLines(request, legalCandidates, finalResults);
    const reachedDepth = Math.min(...finalResults.map((result) => result.depth));
    const nodes = finalResults.reduce((total, result) => total + result.nodes, 0);
    const artifact: BrowserEngineArtifact = {
      ...request,
      engine: this.stockfishProvenance(worker),
      reached: { depth: reachedDepth, nodes, timeMs: Math.max(0, Math.round(this.now() - startedAt)) },
      lines,
      snapshots,
      createdAt: new Date().toISOString(),
    };
    onProgress?.({
      requestId: request.requestId,
      phase: "complete",
      depth: reachedDepth,
      targetDepth: request.limits.depth,
      nodes,
    });
    return artifact;
  }

  private comparisonLines(
    request: BrowserEngineRequest,
    candidates: readonly { fen: string; sans: string[] }[],
    results: readonly PositionEval[],
  ): BrowserEngineLine[] {
    return candidates
      .map((candidate, index): BrowserEngineLine => {
        const result = results[index];
        if (!result) throw new Error("Nikola calculation did not evaluate every requested move.");
        const raw = [...result.pvLines].sort((left, right) => left.rank - right.rank)[0] ?? {
          rank: 1,
          depth: result.depth,
          cp: result.cp,
          mate: result.mate,
          moveUci: result.bestMove,
          continuation: result.pv.slice(1),
        };
        const continuation = replay(
          candidate.fen,
          [raw.moveUci, ...raw.continuation].slice(0, request.limits.maxPlies - candidate.sans.length),
        );
        if (!continuation) throw new Error("Nikola calculation returned an illegal line.");
        const samePerspective = playerToMove(candidate.fen, request.source.playerColor);
        return {
          rank: 1,
          moves: [...candidate.sans, ...continuation.sans],
          evaluation: normalizeEvaluation(raw.cp, raw.mate, samePerspective),
          ...(raw.wdl ? { wdl: normalizeWdl(raw.wdl, samePerspective) } : {}),
        };
      })
      .sort((left, right) => evaluationValue(right.evaluation) - evaluationValue(left.evaluation))
      .map((line, index) => ({ ...line, rank: index + 1 }));
  }

  private async runTablebase(
    request: BrowserEngineRequest,
    active: ActiveCalculation,
    startedAt: number,
    onProgress?: (progress: BrowserEngineProgress) => void,
  ): Promise<BrowserEngineArtifact> {
    if (piecesInPosition(request.source.fen).length > 7)
      throw new Error("Nikola exact endgame evidence supports positions with seven pieces or fewer.");
    const { signal } = active.controller;
    const probe = await abortable(this.tablebase.probe(request.source.fen, signal), signal);
    if (signal.aborted || this.active !== active) throw cancellation();
    const sideOutcome = this.tablebaseOutcome(probe);
    const samePerspective = playerToMove(request.source.fen, request.source.playerColor);
    const outcome = samePerspective
      ? sideOutcome
      : sideOutcome === "win"
        ? "loss"
        : sideOutcome === "loss"
          ? "win"
          : "draw";
    const best = probe.moves[0];
    const applied = best ? replay(request.source.fen, [best.uci]) : null;
    if (best && !applied) throw new Error("Nikola exact endgame evidence returned an illegal move.");
    const playerDtm = probe.dtm == null ? null : samePerspective ? probe.dtm : -probe.dtm;
    const mateMoves =
      playerDtm == null || playerDtm === 0 ? null : Math.sign(playerDtm) * Math.ceil(Math.abs(playerDtm) / 2);
    const evaluation: BrowserEngineLine["evaluation"] =
      mateMoves == null
        ? { cp: outcome === "win" ? 100_000 : outcome === "loss" ? -100_000 : 0, mate: null }
        : { cp: null, mate: mateMoves };
    const wdl =
      outcome === "win"
        ? { win: 1000, draw: 0, loss: 0 }
        : outcome === "loss"
          ? { win: 0, draw: 0, loss: 1000 }
          : { win: 0, draw: 1000, loss: 0 };
    const lines: BrowserEngineLine[] = applied ? [{ rank: 1, moves: applied.sans, evaluation, wdl }] : [];
    const artifact: BrowserEngineArtifact = {
      ...request,
      engine: { name: "Lichess tablebase", version: "standard", build: LICHESS_TABLEBASE_BUILD },
      reached: { depth: 1, nodes: 0, timeMs: Math.max(0, Math.round(this.now() - startedAt)) },
      lines,
      tablebase: {
        outcome,
        category: probe.category,
        dtz: probe.dtz,
        dtm: probe.dtm,
        precise: probe.preciseDtz !== null,
        checkmate: probe.checkmate,
        stalemate: probe.stalemate,
        insufficientMaterial: probe.insufficientMaterial,
      },
      createdAt: new Date().toISOString(),
    };
    onProgress?.({
      requestId: request.requestId,
      phase: "complete",
      depth: 1,
      targetDepth: 1,
      nodes: 0,
    });
    return artifact;
  }

  private tablebaseOutcome(probe: BrowserTablebaseProbe): "win" | "draw" | "loss" {
    if (probe.category === "win" || probe.category === "syzygy-win") return "win";
    if (probe.category === "loss" || probe.category === "syzygy-loss") return "loss";
    return "draw";
  }

  private stockfishProvenance(
    worker: BrowserEngineWorker,
  ): Extract<BrowserEngineArtifact["engine"], { name: "Stockfish" }> {
    return {
      name: "Stockfish",
      version: "18",
      build:
        worker.engineBuild ??
        getEngineUrl(this.workerPreset ?? PRESETS[1])
          .split("/")
          .at(-1) ??
        "stockfish-18-browser",
    };
  }
}

export const browserEngine = new BrowserEngineService();
